import { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Text,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Progress,
  Portal,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  VStack,
  HStack,
  Badge,
  Divider,
  Icon,
} from '@chakra-ui/react';
import { ChevronDownIcon, RepeatIcon } from '@chakra-ui/icons';
import { useToast } from '@chakra-ui/react';
import Chatbot from '../../components/ChatBot/Chatbot';
import { WarningIcon } from '@chakra-ui/icons';
import { Link, Spinner, Center } from '@chakra-ui/react';
import { StorageProviderSelector, StorageProvider } from '../../components/upload/StorageProviderSelector';
import { CloudStorageURLInput } from '../../components/upload/CloudStorageURLInput';
import { StorageProviderInstructions } from '../../components/upload/StorageProviderInstructions';
import { FiFolder } from 'react-icons/fi';
import { BACKEND_API } from '../../constants/backendApiEndpoints';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import FeedbackModal from '../../components/common/FeedbackModal';
import { useFeedback } from '../../hooks/useFeedback';

// Configure PDF worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;


// shared chat session id
function getOrCreateSessionId(): string {
  const KEY = 'chat_session_id';
  let sid = localStorage.getItem(KEY);
  if (!sid) {
    if (window.crypto && 'randomUUID' in window.crypto) {
      sid = window.crypto.randomUUID();
    } else {
      sid = `sid_${Math.random().toString(36).slice(2)}_${Date.now()}`;
    }
    localStorage.setItem(KEY, sid);
  }
  return sid;
}

const ID_RE = /^[A-Z0-9_-]{3,32}$/;
const normalizeId = (v: string) => v.replace(/\s+/g, '').toUpperCase();

const PGXExtractor = () => {

  // --- auto-attach PGX report for chatbot ---
  async function uploadReportFromUrl(url: string) {
    try {
      // Trying to fetch the PDF bytes
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp.ok) throw new Error(`fetch failed: ${resp.status}`);

      const blob = await resp.blob();

      const filename =
        url.split('?')[0].split('/').pop()?.replace(/[#?].*$/, '') ||
        'report.pdf';

      const file = new File([blob], filename, {
        type: blob.type || 'application/pdf',
      });

      const fd = new FormData();
      fd.append('file', file);

      const sessionId = getOrCreateSessionId();

      // Using same API base pattern as chat.ts
      const apiBase = import.meta.env.VITE_API_BASE ?? 'http://127.0.0.1:8000';
      const res = await fetch(`${apiBase}/api/chat/upload-report`, {
        method: 'POST',
        headers: {
          'X-Session-Id': sessionId,
        },
        body: fd,
      });

      if (!res.ok) {
        const txt = await res.text();
        console.warn('upload-report failed:', txt);
      } else {
        console.log('PGX report attached for chatbot session:', sessionId);
      }
    } catch (err) {
      console.warn('Auto PDF upload for chatbot failed (likely CORS):', err);
    }
  }

  // state & refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const patientInputRef = useRef<HTMLInputElement>(null);
  const sampleInputRef = useRef<HTMLInputElement>(null);
  
  // Upload UUID ref for folder organization (similar to HaplotypeCaller)
  const uploadUuidRef = useRef<string | null>(null);
  
  // Generate UUID synchronously to prevent race conditions
  const getOrCreateUploadUuid = (): string => {
    if (!uploadUuidRef.current) {
      uploadUuidRef.current = crypto.randomUUID();
    }
    return uploadUuidRef.current;
  };

  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [, setUploadedFile] = useState<File | null>(null);
  const [uploadedFileId, setUploadedFileId] = useState<number | null>(null);

  const [selectedGenome, setSelectedGenome] = useState('');
  const [isAligning, setIsAligning] = useState(false);
  const [genePanels, setGenePanels] = useState<{ [key: string]: string[] }>({});
  const [selectedPanel, setSelectedPanel] = useState('');
  const [selectedGenes, setSelectedGenes] = useState<string[]>([]);
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [isGeneMenuOpen, setIsGeneMenuOpen] = useState(false);
  const [jobId, setJobId] = useState<number | null>(null);
  const [reportURL, setReportURL] = useState<string | null>(null);
  const [tsvURL, setTsvURL] = useState<string | null>(null);
  const [statusDetail, setStatusDetail] = useState<string>('');
  const [runNumber, setRunNumber] = useState<number | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);

  // Previous files state
  interface PreviousFile {
    key: string;
    filename: string;
    relative_path: string;
    pipeline_folder: string;
    file_type: 'bam' | 'cram';
    size: number;
    last_modified: string | null;
  }
  const [previousFiles, setPreviousFiles] = useState<PreviousFile[]>([]);
  const [isLoadingPreviousFiles, setIsLoadingPreviousFiles] = useState(false);
  const [selectedS3Path, setSelectedS3Path] = useState<string | null>(null);
  const { isOpen: isPreviousFilesOpen, onOpen: onOpenPreviousFiles, onClose: onClosePreviousFiles } = useDisclosure();

  // Feedback modal
  const { showFeedbackModal, setShowFeedbackModal } = useFeedback({
    jobId,
    pipelineType: 'PROPEX',
    jobStatus: isComplete ? 'completed' : '',
    delayMs: 5000,
  });

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
  }

  // reference genomes
  const [referenceGenomes] = useState<{ label: string; value: string }[]>([
    { label: 'DRAGEN_hg19', value: 'DRAGEN_hg19' },
    { label: 'DRAGEN_hg38', value: 'DRAGEN_hg38' },
    { label: 'Ensembl_GRCh37', value: 'Ensembl_GRCh37' },
    { label: 'Ensembl_GRCh38', value: 'Ensembl_GRCh38' },
    // Labels state the real assembly: NCBI build 37.x is GRCh37, despite the
    // stored value reading "GRCh38". The value must stay as-is - it is the enum key.
    { label: 'NCBI build 37.2 (GRCh37)', value: 'NCBI GRCh38 37.2' },
    { label: 'NCBI build 37.1 (GRCh37)', value: 'NCBI GRCh38 37.1' },
    { label: 'NCBI GRCh38Decoy', value: 'NCBI GRCh38Decoy' },
    { label: 'UCSC_hg19_v2', value: 'UCSC_hg19_v2' },
    { label: 'UCSC_hg38', value: 'UCSC_hg38' },
  ]);

  const [localPatientId, setLocalPatientId] = useState('');
  const [localSampleId, setLocalSampleId] = useState('');

  // Cloud storage upload state
  const [storageProvider, setStorageProvider] = useState<StorageProvider>('local');
  const [cloudStorageURL, setCloudStorageURL] = useState('');

  const normalizedPatientId = normalizeId(localPatientId);
  const normalizedSampleId = normalizeId(localSampleId);

  const pidOk = !normalizedPatientId || ID_RE.test(normalizedPatientId);
  const sidOk = !!normalizedSampleId && ID_RE.test(normalizedSampleId);
  const idsValid = pidOk && sidOk;

  const toast = useToast();
  const showToast = (
    title: string,
    status: 'info' | 'warning' | 'success' | 'error'
  ) =>
    toast({ title, status, duration: 5000, isClosable: true, position: 'top' });

  // caret-preserving input handler
  const handleTextChange =
    (ref: React.RefObject<HTMLInputElement>, setValue: (v: string) => void) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value, selectionStart } = e.target;
        setValue(value);
        requestAnimationFrame(() => {
          if (ref.current) {
            ref.current.focus();
            const pos = selectionStart ?? value.length;
            try {
              ref.current.setSelectionRange(pos, pos);
            } catch { }
          }
        });
      };

  // label transform
  const transformPanelLabel = (raw: string) =>
    raw.replace(/panel/gi, 'Panel').replace(/mgmt/gi, 'Management');

  // URL search params for direct navigation
  const [searchParams] = useSearchParams();

  // Handle direct navigation with jobId param (from dashboard)
  useEffect(() => {
    const urlJobId = searchParams.get('jobId');
    const urlView = searchParams.get('view');
    if (urlJobId && urlView === 'completed') {
      const parsedJobId = parseInt(urlJobId, 10);
      if (!isNaN(parsedJobId)) {
        setJobId(parsedJobId);
        setIsComplete(true);
        setProgress(100);
        fetchReport(parsedJobId);
      }
    }
  }, [searchParams]);

  // load panels
  useEffect(() => {
    fetch(`${import.meta.env.VITE_API_URL}/api/v1/genes/panels`)
      .then((res) => res.json())
      .then((data) => setGenePanels(data || {}))
      .catch((err) => console.error('Failed to load gene panels', err));
  }, []);

  // job status types & progress map
  type JobStatus =
    | 'queued'
    | 'initialized'
    | 'validating'
    | 'running'
    | 'processing'
    | 'analyzing'
    | 'completed'
    | 'failed'
    | 'cancelled';

  const progressMap: Record<JobStatus, number> = {
    queued: 5,
    initialized: 10,
    validating: 20,
    running: 40,
    processing: 60,
    analyzing: 80,
    completed: 100,
    failed: 100,
    cancelled: 100,
  };


  // When the pipeline is complete and we have a report URL,
  // automatically upload the PDF to the chatbot's vector store.
  useEffect(() => {
    if (!isComplete) return;
    if (!reportURL) return;

    // Optional: toast so users know they can ask about this report
    showToast('Report is now available to the chatbot.', 'success');

    uploadReportFromUrl(reportURL);
  }, [isComplete, reportURL]);

  // poll job status
  useEffect(() => {
    if (!jobId) return;
    const token = localStorage.getItem('access_token');
    const pollInterval = setInterval(async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/pgx/jobs/${jobId}/status`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const statusData = await res.json();
        if (statusData.detail) setStatusDetail(statusData.detail);
        const status = statusData.status as JobStatus;

        // Use server-provided progress if available, else static map
        const newProgress = (typeof statusData.progress === 'number' && statusData.progress > 0)
          ? statusData.progress
          : (progressMap[status] ?? 0);

        // Ensure monotonic progress (prevent backward jumps)
        setProgress(prev => Math.max(prev, newProgress));
        if (status === 'completed') {
          clearInterval(pollInterval);
          // Wait for report to be fetched before showing completion UI
          fetchReport(jobId).then((success) => {
            if (success) {
              setIsComplete(true);
            } else {
              // Still show completion UI but with warning
              setIsComplete(true);
              showToast('Report may not be available yet. Please wait a moment and refresh.', 'warning');
            }
          });
        } else if (status === 'failed') {
          clearInterval(pollInterval);
          showToast('Job failed during processing.', 'error');
          setIsAligning(false);
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 4000);

    return () => clearInterval(pollInterval);
  }, [jobId]);

  // When report URL is available, auto-upload it for the Chatbot
  //useEffect(() => {
  //if (!reportURL) return;
  //uploadReportFromUrl(reportURL);
  //}, [reportURL]);

  // Helper function to get file extension for backend validation
  const getFileExtension = (filename: string): string => {
    const extension = filename.toLowerCase().split('.').pop();
    switch (extension) {
      case 'bam':
        return 'bam';
      case 'cram':
        return 'cram';
      case 'fastq':
        return 'fastq';
      default:
        return 'application/octet-stream';
    }
  };

  // S3 Multipart upload functions for PropEx
  const initiateS3PropexUpload = async (fileName: string, contentType: string, uploadUuid?: string) => {
    const token = localStorage.getItem('access_token');
    const res = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/s3-propex/uploads/multipart/initiate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        filename: fileName,
        content_type: contentType,
        patient_id: normalizedPatientId,
        sample_id: normalizedSampleId,
        upload_uuid: uploadUuid,
      }),
    });

    if (!res.ok) throw new Error('Failed to initiate upload');
    return await res.json(); // { upload_id, key }
  };

  const getS3PartPresignedUrl = async (key: string, uploadId: string, partNumber: number) => {
    const token = localStorage.getItem('access_token');
    const params = new URLSearchParams({
      key,
      upload_id: uploadId,
      part_number: partNumber.toString(),
    });
    const res = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/s3-propex/uploads/multipart/presign-part?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`Failed to get presigned URL for part ${partNumber}`);
    return await res.json(); // { upload_url, part_number, method }
  };

  const completeS3PropexUpload = async (uploadId: string, key: string, parts: { PartNumber: number; ETag: string }[], filename: string, fileType: string) => {
    const token = localStorage.getItem('access_token');
    const res = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/s3-propex/uploads/multipart/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ upload_id: uploadId, key, parts, filename, file_type: fileType }),
    });
    if (!res.ok) throw new Error('Failed to complete upload');
    return await res.json(); // { file_id, filename, key, size }
  };

  // const handleLocalFileUpload = async (event: any) => {
  //   const file = event.target.files?.[0];
  //   if (!file) return;
  //   if (!idsValid) {
  //     showToast('Please enter valid Patient ID and Sample ID before uploading.', 'warning');
  //     return;
  //   }

  //   setUploadedFile(file);
  //   setUploadedFileName(null);
  //   setProgress(0);
  //   setIsAligning(true);
  //   setStatusDetail('Uploading...');

  //   try {
  //     const fileExtension = getFileExtension(file.name);
  //     const { upload_url, blob_name } = await initiateAzureUpload(file.name, fileExtension);
  //     console.log(upload_url)
  //     const blockIds = await uploadFileInBlocks(file, upload_url);
  //     const confirmData = await commitBlocks(upload_url, blockIds);
  //     console.log(confirmData)

  //     const token = localStorage.getItem('access_token');
  //     console.log(file.type, file.name)
  //     const dbConfirm = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/azure/uploads/confirm-upload`, {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  //       body: JSON.stringify({ s3_key: blob_name, filename: file.name, file_type: fileExtension }),
  //     });
  //     const dbData = await dbConfirm.json();
  //     if (dbData.file_id) setUploadedFileId(dbData.file_id);

  //     if (dbData.file_id) {
  //       setUploadedFileId(dbData.file_id);
  //       setProgress(100);
  //       setUploadedFileName(file.name);
  //       setStatusDetail('Upload complete');
  //     }

  //     setIsAligning(false);
  //   } catch (err) {
  //     console.error('Upload failed:', err);
  //     if (err instanceof Error) {
  //       showToast(`Upload failed: ${err.message}`, 'error');
  //     } else {
  //       showToast('Upload failed', 'error');
  // }
  //     setIsAligning(false);
  //     setUploadedFile(null);
  //     setUploadedFileName(null);
  //     setUploadedFileId(null);
  //     setProgress(0);
  //     setStatusDetail('');
  //   }
  // };


  const handleCloudURLSubmit = async () => {
    if (!cloudStorageURL.trim() || !idsValid) {
      showToast('Please enter a valid URL and Patient/Sample IDs', 'warning');
      return;
    }

    const urlFileName = cloudStorageURL.split('/').pop()?.split('?')[0] || 'cloud_file.cram';
    const apiBase = import.meta.env.VITE_API_URL ?? 'http://localhost:8800';
    const token = localStorage.getItem('access_token');

    setIsAligning(true);
    setProgress(0);
    setStatusDetail('Transferring file from cloud storage...');

    try {
      // Generate or get existing upload UUID for folder organization
      const uploadUuid = getOrCreateUploadUuid();
      
      // Start async transfer to our S3
      const response = await fetch(`${apiBase}/api/v1/s3-propex/uploads/transfer-from-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          source_url: cloudStorageURL,
          filename: urlFileName,
          patient_id: normalizedPatientId,
          sample_id: normalizedSampleId,
          file_type: 'cram',
          upload_uuid: uploadUuid,
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Transfer failed' }));
        throw new Error(error.detail || 'Failed to start transfer');
      }

      const { transfer_id } = await response.json();

      // Poll for transfer status
      let completed = false;
      while (!completed) {
        await new Promise(resolve => setTimeout(resolve, 1500));

        const statusRes = await fetch(
          `${apiBase}/api/v1/s3-propex/uploads/transfer-from-url/${transfer_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!statusRes.ok) {
          throw new Error('Failed to get transfer status');
        }

        const status = await statusRes.json();
        setProgress(status.progress || 0);

        if (status.status === 'completed') {
          completed = true;
          setUploadedFileId(status.file_id);
          setUploadedFileName(urlFileName);
          setStatusDetail('Transfer complete');
          showToast('File transferred successfully!', 'success');
        } else if (status.status === 'failed') {
          throw new Error(status.error || 'Transfer failed');
        }
        // else continue polling (pending, downloading, uploading)
      }
    } catch (error: any) {
      console.error('Cloud transfer error:', error);
      showToast(error.message || 'Failed to transfer file', 'error');
      setStatusDetail('');
    } finally {
      setIsAligning(false);
    }
  };

  const handleLocalFileUpload = async (event: any) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!idsValid) {
      showToast(
        'Please enter valid Patient ID and Sample ID before uploading.',
        'warning'
      );
      return;
    }

    setUploadedFile(file);
    setProgress(0);
    setIsAligning(true);
    setStatusDetail('Uploading...');

    try {
      const fileExtension = getFileExtension(file.name);
      const PART_SIZE = 50 * 1024 * 1024; // 50MB parts
      const CONCURRENT_UPLOADS = 4; // Upload 4 parts in parallel
      
      // Generate or get existing upload UUID for folder organization
      const uploadUuid = getOrCreateUploadUuid();
      
      // Initiate multipart upload
      const { upload_id, key } = await initiateS3PropexUpload(file.name, fileExtension, uploadUuid);
      
      // Calculate number of parts
      const numParts = Math.ceil(file.size / PART_SIZE);
      const parts: { PartNumber: number; ETag: string }[] = new Array(numParts);
      let uploadedBytes = 0;
      
      // Upload a single part
      const uploadPart = async (partNumber: number): Promise<void> => {
        const start = (partNumber - 1) * PART_SIZE;
        const end = Math.min(start + PART_SIZE, file.size);
        const chunk = file.slice(start, end);
        
        // Get presigned URL for this part
        const { upload_url } = await getS3PartPresignedUrl(key, upload_id, partNumber);
        
        // Upload the part
        const etag = await new Promise<string>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', upload_url, true);
          
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              const etag = xhr.getResponseHeader('ETag');
              if (etag) {
                resolve(etag.replace(/"/g, ''));
              } else {
                reject(new Error('No ETag in response'));
              }
            } else {
              reject(new Error(`Part upload failed: ${xhr.status}`));
            }
          };
          
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.send(chunk);
        });
        
        parts[partNumber - 1] = { PartNumber: partNumber, ETag: etag };
        uploadedBytes += (end - start);
        setProgress(Math.round((uploadedBytes / file.size) * 100));
      };
      
      // Upload parts in parallel batches
      for (let i = 0; i < numParts; i += CONCURRENT_UPLOADS) {
        const batch = [];
        for (let j = i; j < Math.min(i + CONCURRENT_UPLOADS, numParts); j++) {
          batch.push(uploadPart(j + 1));
        }
        await Promise.all(batch);
      }
      
      // Complete multipart upload
      const result = await completeS3PropexUpload(upload_id, key, parts, file.name, fileExtension);
      
      if (result.file_id) setUploadedFileId(result.file_id);
      setUploadedFileName(file.name);
      setStatusDetail('Upload complete');
      setIsAligning(false);
    } catch (err) {
      console.error('Upload failed:', err);
      showToast('Upload failed', 'error');
      setIsAligning(false);
    }
  };



  // fetch report URL
  const fetchReport = async (jobId: number, retries: number = 3): Promise<boolean> => {
    const token = localStorage.getItem('access_token');
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/pgx/jobs/${jobId}/results`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Failed to fetch report:', errorData);
        if (retries > 0) {
          // Retry after 2 seconds
          await new Promise(resolve => setTimeout(resolve, 2000));
          return fetchReport(jobId, retries - 1);
        }
        showToast(`Failed to fetch report: ${errorData.detail || 'Unknown error'}`, 'error');
        return false;
      }

      const data = await res.json();
      console.log('Report data received:', data); // Debug log

      if (data.files && typeof data.files === 'object') {
        // Filter out null/undefined values and find PDF
        const pdfKey = Object.keys(data.files).find((k) =>
          k.endsWith('.pdf') && data.files[k] != null && data.files[k] !== ''
        );
        // Find TSV file
        const tsvKey = Object.keys(data.files).find((k) =>
          k.endsWith('.tsv') && data.files[k] != null && data.files[k] !== ''
        );
        if (tsvKey && data.files[tsvKey]) {
          console.log('TSV URL found:', tsvKey, data.files[tsvKey]);
          setTsvURL(data.files[tsvKey]);
        }
        if (pdfKey && data.files[pdfKey]) {
          console.log('PDF URL found:', pdfKey, data.files[pdfKey]); // Debug log
          setReportURL(data.files[pdfKey]);
          return true;
        } else {
          const availableFiles = Object.keys(data.files).filter(k => data.files[k] != null);
          console.warn('No valid PDF URL found. Available files:', availableFiles); // Debug log
          if (retries > 0) {
            // Retry after 2 seconds - PDF might not be ready yet
            await new Promise(resolve => setTimeout(resolve, 2000));
            return fetchReport(jobId, retries - 1);
          }
          showToast('PDF report not found in results. Please try refreshing.', 'warning');
          return false;
        }
      } else {
        console.error('Invalid response format - files object missing:', data);
        if (retries > 0) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          return fetchReport(jobId, retries - 1);
        }
        showToast('Invalid response format from server.', 'error');
        return false;
      }
    } catch (err) {
      console.error('Failed to fetch report URL', err);
      if (retries > 0) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        return fetchReport(jobId, retries - 1);
      }
      showToast('Network error while fetching report. Please try again.', 'error');
      return false;
    }
  };

  // Fetch previous files from S3
  const fetchPreviousFiles = async () => {
    setIsLoadingPreviousFiles(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(BACKEND_API.PGX.PREVIOUS_FILES, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch previous files');
      }

      const data = await res.json();
      setPreviousFiles(data.files || []);
    } catch (err) {
      console.error('Error fetching previous files:', err);
      showToast('Failed to load previous files', 'error');
    } finally {
      setIsLoadingPreviousFiles(false);
    }
  };

  // Select a previous file
  const selectPreviousFile = (file: PreviousFile) => {
    setUploadedFileName(file.filename);
    // Store the S3 key as the path for use in job submission
    setSelectedS3Path(`s3://seq4life-storage/${file.key}`);
    setUploadedFileId(null); // No file upload record ID, using S3 path directly
    setStorageProvider('previous');
    
    showToast(`Selected: ${file.filename}`, 'success');
    onClosePreviousFiles();
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // start analysis
  const handleAlignment = async () => {
    // Check if we have a file - either uploaded or from previous files
    const hasFileFromUpload = !!uploadedFileId;
    const hasFileFromPrevious = !!selectedS3Path;
    
    if ((!hasFileFromUpload && !hasFileFromPrevious) || !selectedGenome || !selectedGenes.length) {
      showToast('Please upload a file and select genome and genes.', 'warning');
      return;
    }
    if (!normalizedSampleId) {
      showToast('Please enter Sample ID.', 'warning');
      return;
    }
    if (!pidOk || !sidOk) {
      showToast(
        'IDs must be 3–32 chars: A–Z, 0–9, underscore, or dash (no spaces).',
        'warning'
      );
      return;
    }
    
    // For previous files, we don't need upload_uuid
    const isUsingPreviousFiles = hasFileFromPrevious && !hasFileFromUpload;
    
    if (!isUsingPreviousFiles && !uploadUuidRef.current) {
      showToast('Upload not completed. Please re-upload the file.', 'error');
      return;
    }

    setIsAligning(true);
    setProgress(5); // Start at 5% (Queued) instead of jumping to 40%
    setStatusDetail('Starting analysis...');

    try {
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      
      if (isUsingPreviousFiles && selectedS3Path) {
        // Using previous files - pass S3 path directly
        formData.append('s3_path', selectedS3Path);
      } else {
        // Normal upload flow
        formData.append('file_id', String(uploadedFileId));
        formData.append('upload_uuid', uploadUuidRef.current!);
      }
      
      formData.append('reference_genome', selectedGenome);
      if (normalizedPatientId) {
        formData.append('patient_id', normalizedPatientId);
      }
      formData.append('sample_id', normalizedSampleId);
      selectedGenes.forEach((gene) => formData.append('genes', gene));

      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/pgx/jobs/run`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      const data = await res.json();
      if (!res.ok) {
        console.error('PGX API error:', data);
        showToast(`API Error: ${data.detail || res.statusText}`, 'error');
        setIsAligning(false);
        return;
      }

      setJobId(data.job_id);
      setRunNumber(data.run_number);
    } catch (err) {
      console.error('PGX request failed:', err);
      showToast(
        'Failed to start PGX analysis. See console for details.',
        'error'
      );
      setIsAligning(false);
    }
  };

  // reset UI
  const resetToStart = () => {
    setUploadedFile(null);
    setUploadedFileName(null);
    setUploadedFileId(null);
    setSelectedS3Path(null);
    setSelectedGenome('');
    setSelectedPanel('');
    setSelectedGenes([]);
    setProgress(0);
    setIsComplete(false);
    setIsGeneMenuOpen(false);
    setJobId(null);
    setReportURL(null);
    setTsvURL(null);
    setStatusDetail('');
    setRunNumber(null);
    setLocalPatientId('');
    setLocalSampleId('');
    setIsAligning(false);
    setStorageProvider('local');
    uploadUuidRef.current = null;  // Reset UUID for new upload session
  };

  // cancel job
  const handleCancelJob = async () => {
    if (!jobId) return;
    const token = localStorage.getItem('access_token');
    if (!token) {
      showToast('You must be logged in to cancel.', 'error');
      return;
    }
    try {
      setIsCancelling(true);
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/pgx/jobs/${jobId}/cancel`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(`Cancel failed: ${data.detail || res.statusText}`, 'error');
        return;
      }
      showToast('Job cancelled successfully.', 'success');
      resetToStart();
    } catch (e) {
      console.error('Cancel failed', e);
      showToast('Cancel request failed.', 'error');
    } finally {
      setIsCancelling(false);
    }
  };

  // UI helpers: pill dropdown
  const FancyDropdownRow = ({
    label,
    placeholder,
    items,
    onSelect,
    selectedValue,
    disabled,
    isInvalid,
    errorText,
  }: {
    label: string;
    placeholder: string;
    items: { label: string; value: string }[];
    onSelect: (value: string) => void;
    selectedValue: string;
    disabled?: boolean;
    isInvalid?: boolean;
    errorText?: string;
  }) => (
    <Flex
      mt={8}
      justify="space-between"
      align="center"
      border="2px solid rgba(0,0,0,0.06)"
      bg="rgba(255, 255, 255, 0.9)"
      borderRadius="3xl"
      boxShadow="lg"
      px={6}
      py={4}
      maxW="600px"
      mx="auto"
      gap={6}
      opacity={disabled ? 0.6 : 1}
      pointerEvents={disabled ? 'none' : 'auto'}
    >
      <Box flex="1">
        <Menu matchWidth>
          <MenuButton
            as={Button}
            isDisabled={disabled}
            fontSize="lg"
            fontWeight="semibold"
            width="100%"
            textAlign="left"
            rightIcon={<ChevronDownIcon boxSize={5} />}
            bg="white"
            color="primary.500"
            border="1px solid rgba(0,0,0,0.06)"
            borderRadius="full"
            px={6}
            py={4}
            boxShadow="sm"
            _hover={{ bg: 'white', color: 'primary.500' }}
            _active={{ bg: 'white', color: 'primary.500' }}
          >
            {items.find((item) => item.value === selectedValue)?.label ||
              placeholder}
          </MenuButton>

          <Portal>
            <MenuList
              maxHeight="260px"
              overflowY="auto"
              borderRadius="xl"
              boxShadow="lg"
              px={2}
              zIndex={9999}
              bg="white"
            >
              {items.map((item) => (
                <MenuItem
                  key={item.value}
                  onClick={() => onSelect(item.value)}
                  fontSize="lg"
                  fontWeight="medium"
                  borderRadius="md"
                  bg="white"
                  _hover={{ bg: 'surface.200' }}
                >
                  {item.label}
                </MenuItem>
              ))}
            </MenuList>
          </Portal>
        </Menu>

        {isInvalid && (
          <Text mt={2} fontSize="sm" color="red.500">
            {errorText || 'Invalid selection'}
          </Text>
        )}
      </Box>

      <Box
        bg="primary.500"
        color="white"
        fontWeight="bold"
        px={6}
        py={3}
        fontSize="lg"
        borderRadius="full"
        boxShadow="md"
      >
        {label}
      </Box>
    </Flex>
  );

  const CustomGeneSelector = () => {
    const allGenes = genePanels['Custom'] || [];
    const allSelected =
      allGenes.length > 0 && selectedGenes.length === allGenes.length;

    const toggleGene = (gene: string) =>
      setSelectedGenes((prev) =>
        prev.includes(gene) ? prev.filter((g) => g !== gene) : [...prev, gene]
      );

    const selectAll = () => setSelectedGenes(allGenes);
    const clearAll = () => setSelectedGenes([]);

    return (
      <Flex
        mt={8}
        justify="space-between"
        align="center"
        border="2px solid rgba(0,0,0,0.06)"
        bg="rgba(255, 255, 255, 0.9)"
        borderRadius="3xl"
        boxShadow="lg"
        px={6}
        py={5}
        maxW="600px"
        mx="auto"
        gap={6}
      >
        <Box flex="1">
          <Menu
            isOpen={isGeneMenuOpen}
            onClose={() => setIsGeneMenuOpen(false)}
            closeOnSelect={false}
          >
            <MenuButton
              as={Button}
              onClick={() => setIsGeneMenuOpen(!isGeneMenuOpen)}
              fontSize="lg"
              fontWeight="semibold"
              width="100%"
              textAlign="left"
              rightIcon={<ChevronDownIcon boxSize={5} />}
              bg="white"
              border="1px solid rgba(0,0,0,0.06)"
              borderRadius="full"
              px={6}
              py={4}
              boxShadow="sm"
              _hover={{ bg: 'surface.100' }}
            >
              {selectedGenes.length > 0
                ? `${selectedGenes.length} genes selected`
                : 'Select Genes'}
            </MenuButton>

            <Portal>
              <MenuList
                maxHeight="260px"
                overflowY="auto"
                borderRadius="xl"
                boxShadow="lg"
                px={2}
                zIndex={9999}
                bg="white"
              >
                <Flex justify="space-between" align="center" px={2} py={1}>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={selectAll}
                    isDisabled={allSelected}
                  >
                    Select All
                  </Button>
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={clearAll}
                    isDisabled={!selectedGenes.length}
                  >
                    Clear
                  </Button>
                </Flex>

                {allGenes.map((gene) => (
                  <MenuItem
                    key={gene}
                    onClick={() => toggleGene(gene)}
                    fontSize="lg"
                    fontWeight="medium"
                    borderRadius="md"
                    bg={selectedGenes.includes(gene) ? 'surface.200' : undefined}
                    _hover={{ bg: 'surface.300' }}
                  >
                    <Flex align="center" gap={2}>
                      <Box
                        w="14px"
                        h="14px"
                        border="2px solid #333"
                        borderRadius="sm"
                        bg={
                          selectedGenes.includes(gene)
                            ? 'primary.500'
                            : 'transparent'
                        }
                      />
                      {gene}
                    </Flex>
                  </MenuItem>
                ))}

                <Box textAlign="center" py={2}>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={() => setIsGeneMenuOpen(false)}
                  >
                    OK
                  </Button>
                </Box>
              </MenuList>
            </Portal>
          </Menu>
        </Box>

        <Box
          bg="primary.500"
          color="white"
          fontWeight="bold"
          px={6}
          py={3}
          fontSize="lg"
          borderRadius="full"
          boxShadow="md"
        >
          Genes
        </Box>
      </Flex>
    );
  };

  const IdInputsRow = () => (
    <Flex mt={8} direction="column" maxW="400px" mx="auto" gap={6}>
      <Box>
        <Text mb={1} fontWeight="medium" fontSize="lg" textAlign="left">
          Sample ID*
        </Text>
        <Input
          placeholder="Enter Sample ID"
          value={localSampleId}
          onChange={handleTextChange(sampleInputRef, setLocalSampleId)}
          ref={sampleInputRef}
          maxLength={32}
          borderColor={localSampleId && !sidOk ? 'red.400' : 'surface.300'}
          focusBorderColor="primary.500"
          autoComplete="off"
          spellCheck={false}
          size="lg"
          bg="white"
          borderRadius="md"
          _hover={{ bg: 'surface.100' }}
          _focus={{ bg: 'white', borderColor: 'primary.500', boxShadow: 'none' }}
          _active={{ bg: 'white' }}
        />
        {localSampleId && !sidOk && (
          <Text mt={1} fontSize="sm" color="red.500" textAlign="left">
            3–32 chars: A–Z, 0–9, _ or -
          </Text>
        )}
      </Box>

      <Box>
        <Text mb={1} fontWeight="medium" fontSize="lg" textAlign="left">
          Patient ID
        </Text>
        <Input
          placeholder="Enter Patient ID"
          value={localPatientId}
          onChange={handleTextChange(patientInputRef, setLocalPatientId)}
          ref={patientInputRef}
          maxLength={32}
          borderColor={localPatientId && !pidOk ? 'red.400' : 'surface.300'}
          focusBorderColor="primary.500"
          autoComplete="off"
          spellCheck={false}
          size="lg"
          bg="white"
          borderRadius="md"
          _hover={{ bg: 'surface.100' }}
          _focus={{ bg: 'white', borderColor: 'primary.500', boxShadow: 'none' }}
          _active={{ bg: 'white' }}
        />
        {localPatientId && !pidOk && (
          <Text mt={1} fontSize="sm" color="red.500" textAlign="left">
            3–32 chars: A–Z, 0–9, _ or -
          </Text>
        )}
      </Box>
    </Flex>
  );

  const logoSrc = '/images/propex%20short%20logo.png';

  return (
    <Box p={8} textAlign="center" position="relative">
      <Flex
        direction="column"
        align="center"
        justify="center"
        mb={8}
        mx="auto"
        maxW="800px"
      >

      </Flex>
      <Image
        src={logoSrc}
        alt="ProPEx"
        position="absolute"
        top={{ base: 0, md: 0 }}
        right={{ base: 0, md: 0 }}
        maxW={{ base: '100px', md: '140px' }}
        minW="48px"
        height="auto"
        objectFit="contain"
        draggable={false}
        pointerEvents="none"
        zIndex={1}
      />

      <Text
        fontSize={{ base: 'md', md: 'lg' }}
        color="primary.500"
        maxW={{ base: '900px', md: '1200px' }}
        mx="auto"
        textAlign={{ base: 'left', md: 'justify' }}
        mb={4}
        pr={{ base: 0, md: '100px' }}
        whiteSpace="normal"
        wordBreak="break-word"
        lineHeight={1.6}
      >
        Unlock the power of pharmacogenetics with ProPEx™. It analyzes up to 87
        genes, including 22 actionable, guideline-associated genes, aligned with
        CPIC, FDA, and DPWG recommendations. Simply upload files from your
        device or import them directly from the cloud, then use our intuitive
        platform to generate a comprehensive genotype–phenotype report. Once
        your report is ready, you can also interact with our agent to explore
        the results in more detail.
      </Text>

      {!uploadedFileName && !isAligning && !isComplete && (
        <>
          <IdInputsRow />

          <Text fontSize="2xl" color="muted.400" mt={8} mb={4}>
            Upload BAM or CRAM file
          </Text>

          {/* Storage Provider Selector */}
          <Box maxW="800px" mx="auto" mt={6}>
            <StorageProviderSelector
              selectedProvider={storageProvider}
              onChange={(provider) => {
                // Check if Sample ID is valid before allowing file selection
                if (!idsValid && provider !== 'local') {
                  showToast('Please enter a valid Sample ID first (3-32 characters: A-Z, 0-9, _ or -)', 'warning');
                  return;
                }
                setStorageProvider(provider);
                // If previous files selected, open modal directly
                if (provider === 'previous') {
                  fetchPreviousFiles();
                  onOpenPreviousFiles();
                }
              }}
              showPrevious={true}
            />
          </Box>

          {/* Local File Upload */}
          {storageProvider === 'local' && (
            <Flex mt={4} justify="center" align="center" maxW="460px" mx="auto">
              <Input
                type="file"
                ref={fileInputRef}
                display="none"
                accept=".bam,.cram"
                onChange={handleLocalFileUpload}
                disabled={!idsValid}
              />
              <Button
                bg="primary.500"
                fontSize="2xl"
                color="white"
                fontWeight="bold"
                px={14}
                py={7}
                borderRadius="full"
                onClick={() => fileInputRef.current?.click()}
                isDisabled={!idsValid}
                _hover={{ bg: 'secondary.500', transform: 'scale(1.03)' }}
                _active={{ bg: 'primary.500', transform: 'scale(0.99)' }}
                transition="all 0.18s ease-in-out"
              >
                Upload your file
              </Button>
            </Flex>
          )}

          {/* Cloud Storage URL Input */}
          {storageProvider !== 'local' && (
            <>
              <CloudStorageURLInput
                provider={storageProvider}
                value={cloudStorageURL}
                onChange={setCloudStorageURL}
                onSubmit={handleCloudURLSubmit}
                isDisabled={!idsValid}
              />
              <StorageProviderInstructions provider={storageProvider} />
            </>
          )}

          {progress > 0 && (
            <Box mt={6} maxW="500px" mx="auto">
              <Progress
                value={progress}
                colorScheme="purple"
                size="lg"
                hasStripe
                isAnimated
                borderRadius="full"
              />
              <Text mt={2} fontSize="md" color="muted.400">
                {progress}% uploaded
              </Text>
            </Box>
          )}
        </>
      )}

      {uploadedFileName && !isAligning && !isComplete && (
        <>
          <Text
            fontSize="2xl"
            mt={10}
            maxW={{ base: '300px', md: '640px' }}
            mx="auto"
            textAlign="center"
            lineHeight="1.25"
          >
            Your file has been uploaded successfully. Please enter the Patient
            ID and Sample ID, then select the Reference Genome and Gene Panel to
            proceed.
          </Text>

          <IdInputsRow />

          <FancyDropdownRow
            label="Reference Genome"
            placeholder="Select Reference Genome"
            items={referenceGenomes}
            onSelect={(v) => setSelectedGenome(v)}
            selectedValue={selectedGenome}
            disabled={!idsValid}
          />

          {/* Warning message - always shown after file upload */}
          <Box
            mt={4}
            p={3}
            bg="surface.300"
            border="1px solid"
            borderColor="muted.400"
            borderRadius="md"
            maxW="600px"
            mx="auto"
          >
            <Flex align="center" gap={2}>
              <WarningIcon color="muted.400" boxSize={5} />
              <Text fontSize="sm" color="primary.500" fontWeight="medium">
                Make sure to select the same reference genome that was used during the
                earlier alignment step; otherwise, the output may be inaccurate. If you
                are unsure which reference genome was used, please contact us on{' '}
                <Link href="https://seq4life.com" color="secondary.500" fontWeight="bold" target="_blank" rel="noopener noreferrer">
                  seq4life.com
                </Link>{' '}
                for assistance.
              </Text>
            </Flex>
          </Box>

          {Object.keys(genePanels).length > 0 && (
            <FancyDropdownRow
              label="Gene Panel"
              placeholder="Select Panel"
              items={Object.keys(genePanels).map((p) => ({
                label: transformPanelLabel(p),
                value: p,
              }))}
              onSelect={(panel) => {
                setSelectedPanel(panel);
                setSelectedGenes(panel !== 'Custom' ? genePanels[panel] : []);
              }}
              selectedValue={selectedPanel}
              disabled={!idsValid}
            />
          )}

          {selectedPanel === 'Custom' && <CustomGeneSelector />}

          {selectedPanel && selectedGenes.length > 0 && (
            <Box mt={6} maxW="600px" mx="auto" textAlign="left">
              <Text mb={2} fontWeight="semibold">
                Selected Genes:
              </Text>
              <Flex wrap="wrap" gap={2}>
                {selectedGenes.map((gene) => (
                  <Box
                    key={gene}
                    px={3}
                    py={1}
                    bg="surface.200"
                    border="1px solid"
                    borderColor="surface.300"
                    borderRadius="md"
                    fontSize="sm"
                    fontWeight="medium"
                    display="flex"
                    alignItems="center"
                    gap={2}
                  >
                    <Text>{gene}</Text>
                    <Button
                      size="xs"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() =>
                        setSelectedGenes((prev) =>
                          prev.filter((g) => g !== gene)
                        )
                      }
                    >
                      ✕
                    </Button>
                  </Box>
                ))}
              </Flex>

              <Button
                mt={4}
                size="sm"
                colorScheme="red"
                variant="outline"
                onClick={() => setSelectedGenes([])}
              >
                Clear All
              </Button>
            </Box>
          )}

          <Button
            mt={10}
            mb="140px"
            bg="primary.500"
            fontSize="2xl"
            color="white"
            fontWeight="bold"
            px={12}
            py={7}
            borderRadius="full"
            onClick={handleAlignment}
            isDisabled={!idsValid || !selectedGenes.length}
          >
            Analyze
          </Button>
        </>
      )}

      {isAligning && !isComplete && (
        <Box textAlign="center" p={8}>
          <Text
            as="h3"
            fontSize="3xl"
            mt={10}
            mx="auto"
            maxW={{ base: '280px', md: '640px' }}
            whiteSpace="normal"
            overflowWrap="break-word"
            lineHeight="1.3"
          >
            {uploadedFileId
              ? 'Mapping out insights – sit tight while we prepare your results'
              : 'Uploading your file...'}
          </Text>

          <Text
            mt={4}
            color="muted.500"
            mx="auto"
            maxW={{ base: '280px', md: '500px' }}
            fontSize="md"
            fontStyle="italic"
          >
            This might take a while. We'll email you when your results are ready – feel free to come back later.
          </Text>

          {!!statusDetail && (
            <Text
              mt={2}
              color="muted.400"
              mx="auto"
              maxW={{ base: '260px', md: '600px' }}
              whiteSpace="normal"
              overflowWrap="break-word"
            >
              {statusDetail}
            </Text>
          )}

          {progress > 0 && !/^\d+%$/.test(statusDetail) && (
            <Text mt={3} mb={1} fontSize="lg" color="primary.500">
              {progress}%
            </Text>
          )}

          <Flex
            mt={10}
            justify="center"
            align="center"
            p={0}
            maxW="500px"
            mx="auto"
            position="relative"
          >
            <Box
              position="relative"
              mt={6}
              maxW="500px"
              w="100%"
              mx="auto"
              h="24px"
              bg="surface.300"
              borderRadius="full"
              overflow="hidden"
            >
              <Box
                position="absolute"
                left={0}
                top={0}
                bottom={0}
                width={`${progress}%`}
                bg="primary.500"
                borderRadius="full"
                transition="width 0.5s ease"
              />
            </Box>
          </Flex>

          {jobId && (
            <Button
              mt={6}
              colorScheme="red"
              onClick={handleCancelJob}
              isLoading={isCancelling}
            >
              Cancel Job
            </Button>
          )}
        </Box>
      )}

      {isComplete && (
        <>
          <Text fontSize="3xl" fontWeight="bold" mt={20}>
            File conversion is complete and ready to be downloaded
          </Text>
          <Box mt={10} maxW="600px" mx="auto">
            <Text fontSize="2xl" fontWeight="bold" textAlign="center">
              {uploadedFileName}
            </Text>
            <Flex mt={4} justify="center" align="center" wrap="wrap" gap={3}>
              <Button
                bg="#1a1262"
                color="white"
                fontWeight="bold"
                width="140px"
                py={4}
                _hover={{ bg: "secondary.500" }}
                onClick={async () => {
                  if (!reportURL) {
                    showToast('Report URL is not available yet.', 'warning');
                    return;
                  }
                  try {
                    // Fetch as blob to force download (download attribute doesn't work cross-origin)
                    const response = await fetch(reportURL);
                    if (!response.ok) throw new Error('Download failed');
                    const blob = await response.blob();
                    const blobUrl = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = blobUrl;
                    a.download = 'ProPEx_Report.pdf';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(blobUrl);
                  } catch (err) {
                    console.error('PDF download failed:', err);
                    showToast('Failed to download PDF', 'error');
                  }
                }}
              >
                Download Report
              </Button>
              <Button
                bg="#1a1262"
                color="white"
                fontWeight="bold"
                width="140px"
                py={4}
                _hover={{ bg: "secondary.500" }}
                onClick={async () => {
                  if (!tsvURL) {
                    showToast('TSV file is not available yet.', 'warning');
                    return;
                  }
                  try {
                    const response = await fetch(tsvURL);
                    if (!response.ok) throw new Error('Download failed');
                    const blob = await response.blob();
                    const blobUrl = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = blobUrl;
                    a.download = 'ProPEx_Results.tsv';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(blobUrl);
                  } catch (err) {
                    console.error('TSV download failed:', err);
                    showToast('Failed to download TSV', 'error');
                  }
                }}
              >
                Download TSV
              </Button>
              <Button
                bg="#1a1262"
                color="white"
                fontWeight="bold"
                width="140px"
                py={4}
                _hover={{ bg: "secondary.500" }}
                onClick={() => {
                  if (!jobId) {
                    showToast('Job ID is not available yet.', 'warning');
                    return;
                  }
                  // Open PDF via proxy endpoint for inline display
                  const token = localStorage.getItem('access_token');
                  window.open(`${import.meta.env.VITE_API_URL}/api/v1/pgx/jobs/${jobId}/pdf?t=${token}`, '_blank', 'noopener,noreferrer');
                }}
              >
                View PDF
              </Button>
              <Button
                bg="#1a1262"
                color="white"
                fontWeight="bold"
                width="140px"
                py={4}
                _hover={{ bg: "secondary.500" }}
                onClick={() => window.open(`/reports/${jobId}?run=${runNumber}`, '_blank', 'noopener,noreferrer')}
              >
                View Summary
              </Button>
              {jobId && !reportURL && (
                <Button
                  variant="outline"
                  borderColor="surface.300"
                  color="primary.500"
                  bg="white"
                  fontWeight="bold"
                  width="140px"
                  py={4}
                  onClick={async () => {
                    showToast('Refreshing report...', 'info');
                    await fetchReport(jobId);
                  }}
                >
                  Refresh Report
                </Button>
              )}
            </Flex>
          </Box>

          <Box
            mt={6}
            width="800px"
            maxW="95%"
            mx="auto"
            sx={{ aspectRatio: '210/297' }}
            border="1px solid #ccc"
            borderRadius="md"
          >
            {jobId ? (
              <Box
                width="100%"
                height="100%"
                bg="surface.200"
                overflowY="auto" // Vertical scroll
                display="flex"
                flexDirection="column"
                alignItems="center" // Center pages horizontally
                py={8} // Top/bottom padding
                css={{
                  // Optional: nice scrollbar
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#888',
                    borderRadius: '4px',
                  },
                }}
              >
                <Document
                  file={`${import.meta.env.VITE_API_URL}/api/v1/pgx/jobs/${jobId}/pdf?t=${localStorage.getItem('access_token')}`}
                  onLoadSuccess={onDocumentLoadSuccess}
                  externalLinkTarget="_blank" // Opens links in new tab
                  loading={
                    <Center h="100%">
                      <Spinner size="xl" color="primary.500" thickness="4px" />
                    </Center>
                  }
                  error={
                    <Center h="100%">
                      <Text color="red.500">Failed to load PDF.</Text>
                    </Center>
                  }
                >
                  {Array.from(new Array(numPages), (_, index) => (
                    <Box
                      key={`page_${index + 1}`}
                      mb={6} // Spacing between pages
                      boxShadow="0 4px 12px rgba(0,0,0,0.15)" // Drop shadow for "paper" feel
                    >
                      <Page
                        pageNumber={index + 1}
                        width={800} // Fixed width for consistent "ratio"
                        renderTextLayer={false}
                        renderAnnotationLayer={true} // Crucial for links
                      />
                    </Box>
                  ))}
                </Document>
              </Box>
            ) : (
              <Box mt={4} textAlign="center" p={8}>
                <Text color="red.500" fontSize="lg" fontWeight="bold" mb={2}>
                  Report preview not available.
                </Text>
                <Text color="muted.400" mb={4}>
                  The PDF report may still be processing. Please wait a moment and click "Refresh Report" above.
                </Text>
                {jobId && (
                  <Button
                    colorScheme="blue"
                    onClick={async () => {
                      showToast('Refreshing report...', 'info');
                      await fetchReport(jobId);
                    }}
                  >
                    Refresh Report
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </>
      )}
      
      {/* Previous Files Modal */}
      <Modal isOpen={isPreviousFilesOpen} onClose={onClosePreviousFiles} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxW="700px">
          <ModalHeader>
            <HStack justify="space-between">
              <Text>Select from Previous Uploads</Text>
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<RepeatIcon />}
                onClick={fetchPreviousFiles}
                isLoading={isLoadingPreviousFiles}
              >
                Refresh
              </Button>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {isLoadingPreviousFiles ? (
              <Center py={10}>
                <Spinner size="xl" color="primary.500" />
              </Center>
            ) : previousFiles.length === 0 ? (
              <Center py={10}>
                <VStack>
                  <Icon as={FiFolder} boxSize={12} color="muted.300" />
                  <Text color="muted.400" fontSize="lg">No previous files found</Text>
                  <Text color="muted.300" fontSize="sm">Upload BAM/CRAM files from other pipelines to reuse them here</Text>
                </VStack>
              </Center>
            ) : (
              <VStack spacing={2} align="stretch">
                {/* Group by pipeline folder */}
                {Object.entries(
                  previousFiles.reduce((acc, file) => {
                    const folder = file.pipeline_folder;
                    if (!acc[folder]) acc[folder] = [];
                    acc[folder].push(file);
                    return acc;
                  }, {} as Record<string, PreviousFile[]>)
                ).map(([folder, files]) => (
                  <Box key={folder}>
                    <Text fontSize="sm" fontWeight="bold" color="muted.500" textTransform="uppercase" mb={2}>
                      {folder.replace(/_/g, ' ')}
                    </Text>
                    <VStack spacing={1} align="stretch">
                      {files.map((file) => (
                        <Flex
                          key={file.key}
                          p={3}
                          bg="surface.50"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="surface.200"
                          cursor="pointer"
                          _hover={{ bg: 'surface.100', borderColor: 'primary.300' }}
                          onClick={() => selectPreviousFile(file)}
                          align="center"
                          justify="space-between"
                        >
                          <HStack spacing={3} flex={1} minW={0}>
                            <Icon as={FiFolder} color="primary.400" />
                            <Text fontWeight="medium" color="text.primary" isTruncated maxW="100%">
                              {file.filename}
                            </Text>
                          </HStack>
                          <HStack spacing={2}>
                            <Badge colorScheme={file.file_type === 'bam' ? 'blue' : 'purple'} fontSize="xs">
                              {file.file_type.toUpperCase()}
                            </Badge>
                            <Text fontSize="xs" color="muted.400" whiteSpace="nowrap">
                              {formatFileSize(file.size)}
                            </Text>
                          </HStack>
                        </Flex>
                      ))}
                    </VStack>
                    <Divider my={3} />
                  </Box>
                ))}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Feedback Modal */}
      {jobId && (
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          jobId={jobId}
          pipelineType="PROPEX"
          onSubmitSuccess={() => setShowFeedbackModal(false)}
        />
      )}
      
      <Box flexShrink={0}>
        <Chatbot />
      </Box>
    </Box>
  );
};

export default PGXExtractor;
